import React, { useEffect, useState, useMemo } from 'react';
import MobileLayout from '../../../components/layout/MobileLayout';
import QRModal from '../../../components/ui/QRModal';
import { getMerchantPaymentSettings, getPaymentLinks, getUserInfo } from '../../../services/useAPICaller';
import { PaymentSession } from '../interfaces';
import { useQRCodePDF } from '../../../services/useQRCodePDF';
import { useToast } from '../../../components/ui/Toast';
import NewPOSWizard from './NewPOSWizard';
import { useI18n } from '../../../i18n/I18n';
import POSSessionCard from '../../../components/ui/POSSessionCard';
export default function AddPosView() {
    const { t } = useI18n();
    const [paymentLinks, setPaymentLinks] = useState<PaymentSession[]>([]);
    const [userInfo, setUserInfo] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [visibleCount, setVisibleCount] = useState<number>(3);
    const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [selectedQrSession, setSelectedQrSession] = useState<PaymentSession | null>(null);
    const [isNewPosWizardOpen, setIsNewPosWizardOpen] = useState(false);

    const openQrModal = (e: React.MouseEvent, session: PaymentSession) => {
        e.stopPropagation();
        setSelectedQrSession(session);
        setIsQrModalOpen(true);
    };

    const closeQrModal = () => {
        setIsQrModalOpen(false);
        setSelectedQrSession(null);
    };

    const { downloadQRCodePDF } = useQRCodePDF();
    const toast = useToast();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('jwtToken');
                if (!token) {
                    setLoading(false);
                    return;
                }

                // Fetch merchant settings
                getMerchantPaymentSettings(() => { });

                // Fetch payment links (sessions)
                const linksData = await getPaymentLinks(0, 50);
                if (linksData && linksData.content) {
                    setPaymentLinks(linksData.content);
                }

                // Fetch user info for authorized features
                const info = await getUserInfo();
                setUserInfo(info);

            } catch (error) {
                console.error("Error fetching POS data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const fetchSessions = async () => {
        try {
            const linksData = await getPaymentLinks(0, 50);
            if (linksData && linksData.content) {
                setPaymentLinks(linksData.content);
            }
        } catch (error) {
            console.error("Error refreshing sessions:", error);
        }
    };

    const authorizedEmails = process.env.REACT_APP_SPRINTCHECKOUT_SPONSOR_ALLOWED_MAILS?.split(',') || [];
    const shouldShowWelcomeCard = () => {
        if (!userInfo?.mail) return false;
        return authorizedEmails.includes(userInfo.mail);
    };

    const handleCopyLink = (sessionId: string) => {
        const link = `https://pay.sprintcheckout.com/?uid=${sessionId}`;
        navigator.clipboard.writeText(link);
        toast({ title: t('pos.linkCopied'), status: "success", duration: 2000 });
    };

    const handleDownloadPDF = async (sessionId: string, sessionName?: string) => {
        try {
            const link = `https://pay.sprintcheckout.com/?uid=${sessionId}`;
            await downloadQRCodePDF(link, { sessionName });
        } catch (err) {
            console.error('Error downloading QR PDF:', err);
        }
    };

    const handleWelcomeCardClick = async (session: PaymentSession) => {
        try {
            await downloadQRCodePDF(
                `https://dashboard.sprintcheckout.com/#/auth/sign-in/default?join=${session.sessionId}`,
                {
                    basePdf: 'spcWelcomeCard',
                    filename: 'Sprintcheckout-welcome-card.pdf',
                    position: { x: 260, y: 565, width: 80, height: 80 },
                }
            );
        } catch (err) {
            console.error('Error downloading welcome card PDF:', err);
        }
    };



    const toggleExpand = (sessionId: string) => {
        setExpandedSessionId(expandedSessionId === sessionId ? null : sessionId);
    };

    const visibleLinks = useMemo(() => {
        return paymentLinks.slice(0, visibleCount);
    }, [paymentLinks, visibleCount]);

    const handleShowMore = () => {
        setVisibleCount(prev => prev + 10);
    };

    return (
        <MobileLayout>
            {/* Header */}
            <div className="px-6 lg:px-10 pt-12 lg:pt-10 pb-2 flex justify-between items-center z-10 w-full max-w-md lg:max-w-5xl">
                <div>
                    <h1 className="text-3xl font-bold text-txt-heading tracking-tight leading-tight">{t('pos.title')}</h1>
                    <p className="text-slate-grey text-[14px] font-medium mt-0.5">{t('pos.subtitle')}</p>
                </div>
            </div>

            {/* New POS Button */}
            <div className="px-6 lg:px-10 py-4 z-10 w-full max-w-md lg:max-w-5xl">
                <button
                    onClick={() => setIsNewPosWizardOpen(true)}
                    className="w-full lg:w-auto lg:px-8 bg-electric-blue border-none hover:bg-electric-blue/90 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-electric-blue/25 transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                >
                    <span className="material-symbols-outlined text-[20px]">add_link</span>
                    {t('pos.newPos')}
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 w-full max-w-md lg:max-w-5xl overflow-y-auto no-scrollbar px-6 lg:px-10 pb-40 lg:pb-10 z-0">
                {loading ? (
                    <div className="space-y-4 animate-pulse">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="glass-panel rounded-2xl p-4" style={{ borderColor: 'var(--color-surface-border)' }}>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl flex-shrink-0" style={{ background: 'var(--color-surface)' }} />
                                        <div className="space-y-2">
                                            <div className="h-3.5 rounded-full w-32" style={{ background: 'var(--color-surface)' }} />
                                            <div className="h-2.5 rounded-full w-20" style={{ background: 'var(--color-surface)' }} />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="h-6 rounded w-24" style={{ background: 'var(--color-surface)' }} />
                                        <div className="w-5 h-5 rounded-full" style={{ background: 'var(--color-surface)' }} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : paymentLinks.length === 0 ? (
                    <div className="py-10 text-center text-txt-secondary">{t('pos.noSessions')}</div>
                ) : (
                    <div className="space-y-4 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4 lg:space-y-0 lg:items-start">
                        {visibleLinks.map((session) => (
                            <POSSessionCard
                                key={session.sessionId}
                                session={session}
                                isExpanded={expandedSessionId === session.sessionId}
                                onToggleExpand={() => toggleExpand(session.sessionId!)}
                                onOpenQrModal={(e) => openQrModal(e, session)}
                                onCopyLink={() => handleCopyLink(session.sessionId!)}
                                onDownloadPDF={() => handleDownloadPDF(session.sessionId!, session.name || session.concept || '')}
                                onWelcomeCard={() => handleWelcomeCardClick(session)}
                                shouldShowWelcomeCard={shouldShowWelcomeCard()}
                            />
                        ))}

                        {visibleCount < paymentLinks.length && (
                            <div className="flex justify-center mt-6">
                                <button
                                    onClick={handleShowMore}
                                    className="glass-btn px-6 py-2.5 rounded-full flex items-center gap-2 text-[13px] font-semibold text-txt-secondary hover:text-txt-heading transition-all border-none cursor-pointer"
                                >
                                    <span>{t('pos.showMore')}</span>
                                    <span className="material-symbols-outlined text-[16px]">expand_more</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Advanced Section */}
                <div className="mt-8 mb-6 lg:hidden">
                    <h4 className="text-xs font-bold text-txt-muted uppercase tracking-widest px-1 mb-3">{t('pos.advanced')}</h4>

                    <div
                        onClick={() => window.location.hash = "#/admin/developers"}
                        className="glass-panel rounded-2xl p-5 border-l-4 border-l-electric-blue bg-gradient-to-r from-electric-blue/5 to-transparent flex items-center gap-4 transition-all hover:bg-surface-hover cursor-pointer group mb-3"
                    >
                        <div className="w-12 h-12 rounded-xl bg-deep-navy flex items-center justify-center group-hover:scale-110 transition-transform" style={{ border: '1px solid var(--color-surface-border)' }}>
                            <span className="material-symbols-outlined text-electric-blue text-[28px]">terminal</span>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-txt-heading font-bold text-[16px]">{t('pos.devZone')}</h3>
                            <p className="text-txt-secondary text-[12px] leading-tight mt-0.5">{t('pos.devZoneDesc')}</p>
                        </div>
                        <span className="material-symbols-outlined text-txt-muted group-hover:text-txt-heading transition-colors">chevron_right</span>
                    </div>

                    <div
                        onClick={() => window.location.hash = "#/admin/woocommerce"}
                        className="glass-panel rounded-2xl p-5 border-l-4 border-l-accent-purple bg-gradient-to-r from-accent-purple/5 to-transparent flex items-center gap-4 transition-all hover:bg-surface-hover cursor-pointer group"
                    >
                        <div className="w-12 h-12 rounded-xl bg-deep-navy flex items-center justify-center group-hover:scale-110 transition-transform" style={{ border: '1px solid var(--color-surface-border)' }}>
                            <span className="material-symbols-outlined text-accent-purple text-[28px]">shopping_cart</span>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-txt-heading font-bold text-[16px]">{t('pos.woocommerceSettings')}</h3>
                            <p className="text-txt-secondary text-[12px] leading-tight mt-0.5">{t('pos.woocommerceDesc')}</p>
                        </div>
                        <span className="material-symbols-outlined text-txt-muted group-hover:text-txt-heading transition-colors">chevron_right</span>
                    </div>
                </div>
            </div>

            {/* QR Code Modal */}
            {isQrModalOpen && selectedQrSession && (
                <QRModal
                    session={selectedQrSession}
                    onClose={closeQrModal}
                    onCopyLink={handleCopyLink}
                    onDownloadPDF={handleDownloadPDF}
                />
            )}

            {/* New POS Wizard */}
            {isNewPosWizardOpen && (
                <NewPOSWizard
                    onClose={() => setIsNewPosWizardOpen(false)}
                    onSuccess={() => {
                        setIsNewPosWizardOpen(false);
                        fetchSessions(); // Refresh list
                    }}
                />
            )}
        </MobileLayout>
    );
}
